import { eulerDegFromQuat, qAxisAngle, qCompose, qRotate, type Quat, type Vec3 } from './assemblyPose';
import type { Fitting } from './assemblyStore';
import type { FitAxis, HoleFit } from './holeFit';
import { faceNormal } from './partFaces';
import type { CoverMark } from './covers';
import type { PartItem } from './partsRun';
import { fastenerKind, type StepHole } from './stepHoles';
import { pieceOf } from './threeDApi';
import { shapeEdits } from './fittingModels';

// HOW A JOINT'S HARDWARE GOES IN, WORKED OUT RATHER THAN ASKED — what the joint view's Exploded
// shows and what the assembly phase's hole rule places (see fromHole).
//
// Find holes already knows, per sub-step, which piece of hardware enters which part, by which face,
// where on it, how deep the wood begins and how far the head is sunk; the product already knows
// where every part stands. So where the hardware rests, which way it comes in and how far out it
// starts are arithmetic on those — the assembly call was asked them again, off renders.

// HOW FAR A PIECE OF HARDWARE STANDS OUT OF ITS HOLE, EXPLODED (the whole product's view): its own
// length, and this.
export const BACK_OFF_MM = 10;
// AND WHERE IT STARTS ITS MOVEMENT IN: its tip this far clear of the hole's mouth — outside the
// part, the sunk stretch still ahead of it.
const START_CLEAR_MM = 3;
// HOW FAR APART A JOINT'S TWO PARTS STAND, EXPLODED, along a piece of hardware of length `len`: the
// piece at the start of its movement (START_CLEAR_MM out of its hole, all of its length) and a margin
// to the other part — so a dowel stands in the gap between the two, in neither. See jointSpread.
export const jointGap = (len: number) => START_CLEAR_MM + len + BACK_OFF_MM;

// HOW DEEP UNDER ITS BOX FACE A PIECE OF HARDWARE'S MIDDLE RESTS, along its hole: where the wood
// begins, the head's sink, as far in as its hole goes (or all of it, through or depthless), less
// half its length. `mouth`: where the wood begins (StepHole.surfaceMm, or a cover's own reading).
export function restDown(h: StepHole, len: number, mouth: number): number {
  const bore = h.through ? null : h.depthMm;
  const inserted = bore == null ? len : Math.min(len, bore);
  return mouth + (h.sunkMm ?? 0) + inserted - len / 2;
}

// The turn that lays direction `a` onto direction `b`, both unit vectors, as [x, y, z, w].
export function quatBetween(a: Vec3, b: Vec3): [number, number, number, number] {
  const d = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  if (d < -0.999999) {
    // Opposite: half a turn about any axis square to `a`.
    const ax: Vec3 = Math.abs(a[0]) < 0.9 ? [0, -a[2], a[1]] : [-a[2], 0, a[0]];
    const l = Math.hypot(...ax);
    return [ax[0] / l, ax[1] / l, ax[2] / l, 0];
  }
  const c: Vec3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const q: [number, number, number, number] = [c[0], c[1], c[2], 1 + d];
  const l = Math.hypot(...q);
  return [q[0] / l, q[1] / l, q[2] / l, q[3] / l];
}

// A PIECE'S TURN ABOUT ITS OWN AXIS, where another piece runs across it — a barrel nut, whose
// threaded hole has to face the screw. Laying its axis down its hole fixes everything but that
// turn, and the shortest way there left the hole pointing anywhere. `own`: the crossing piece's
// direction in THIS piece's model frame; `to`: that direction in the scene. `either`: `own` is a bore
// read off this piece's mesh (see crossBoreOf), open at both ends, so it may face either way.
export type Across = { own: Vec3; to: Vec3; either?: boolean };

// KNOWN WHERE BOTH WERE CUT FROM ONE SET, untouched since: each kept piece is still in the set's
// frame (see _keep_pieces), where they were built fitted together — so the screw's axis in its own
// file IS the screw's axis in the nut's. Null otherwise, and the nut keeps the shortest turn.
export function acrossOf(fit: HoleFit, crossing: HoleFit, crossingAxis: FitAxis, crossingHole: StepHole): Across | null {
  const a = pieceOf(fit.job);
  const b = pieceOf(crossing.job);
  if (a?.from !== 'set' || b?.from !== 'set' || a.of !== b.of) return null;
  if (shapeEdits(fit.job) > 0 || shapeEdits(crossing.job) > 0) return null;
  const n = faceNormal(crossingHole.face);
  return { own: crossingAxis.into, to: [-n[0], -n[1], -n[2]] };
}

// AND WHERE THEY WERE NOT: the bore read off the piece's own mesh (see crossBoreOf), turned to the
// crossing piece's hole. Its set does not have to have built the two together.
export const acrossBore = (bore: Vec3, crossingHole: StepHole): Across => {
  const n = faceNormal(crossingHole.face);
  return { own: bore, to: [-n[0], -n[1], -n[2]], either: true };
};

// HOW A PIECE STANDS IN ITS HOLE: its axis down it, and turned about it where something crosses it.
export function holeTurn(axis: { into: Vec3; even: boolean }, inward: Vec3, flip = false, across?: Across | null): Quat {
  const q = quatBetween(intoHole(axis, inward, flip), inward);
  if (!across) return q;
  const flat = (v: Vec3): Vec3 => {
    const d = v[0] * inward[0] + v[1] * inward[1] + v[2] * inward[2];
    return [v[0] - inward[0] * d, v[1] - inward[1] * d, v[2] - inward[2] * d];
  };
  const turned = flat(qRotate(q, across.own));
  const b = flat(across.to);
  // A bore faces the screw from either mouth: the nearer one, the shorter turn.
  const a: Vec3 = across.either && turned[0] * b[0] + turned[1] * b[1] + turned[2] * b[2] < 0
    ? [-turned[0], -turned[1], -turned[2]] : turned;
  if (Math.hypot(...a) < 1e-6 || Math.hypot(...b) < 1e-6) return q;
  const x: Vec3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const angle = Math.atan2(x[0] * inward[0] + x[1] * inward[1] + x[2] * inward[2], a[0] * b[0] + a[1] * b[1] + a[2] * b[2]);
  return qCompose(qAxisAngle(inward, angle), q);
}

// WHICH WAY THE HARDWARE'S OWN AXIS POINTS IN ITS HOLE: its axis as its mesh has it, the short way
// round where both ends are alike (a dowel, a pad) — and the other way where it was turned over.
export const intoHole = (axis: { into: Vec3; even: boolean }, inward: Vec3, flip = false): Vec3 => {
  const along = axis.into[0] * inward[0] + axis.into[1] * inward[1] + axis.into[2] * inward[2];
  return (axis.even && along < 0) !== flip ? [-axis.into[0], -axis.into[1], -axis.into[2]] : axis.into;
};

// ONE PIECE OF HARDWARE'S MOVEMENT INTO ITS HOLE, as the assembly phase stores one — centre to
// centre from the part it enters, both standing on their boxes' middles: resting in the hole,
// turned down it, coming in along it from just outside the part's mouth, wound in all the way at
// its thread's lead where it is a screw — measured on its model (see threadLeadOf); a screw whose
// thread was not read goes in straight, and the caller says so (see unthreaded). `onFace`: the
// hole's mouth on the part's box face, from its middle; `mouth`: where the wood begins under it.
//
// A COVER (CoverMark) THE SAME WAY, as the joint view stands it: less the way the covers call moved
// it out of its part, turned over where it turned it, pushed straight on — from BACK_OFF_MM clear,
// since a pad that rests ON the face would otherwise travel a few millimetres and seem not to move.
const ACROSS_TURN_DEG = 90;
export type HoleMovement = Pick<Fitting, 'seat' | 'spin' | 'axis' | 'travel' | 'turn' | 'drive' | 'pitch' | 'depth'>;
export function holeMovement(
  h: StepHole,
  axis: FitAxis,
  onFace: Vec3,
  moving: PartItem | undefined,
  mouth = h.surfaceMm ?? 0,
  across?: Across | null,
  // HOW FAR THE THREAD BITES, where the caller knows: the screw then slides home through the holes
  // it only passes, and turns for this last stretch (see biteOf). Unknown: it turns all the way.
  bite?: number | null,
): HoleMovement {
  const cover = h as Partial<CoverMark>;
  const n = faceNormal(h.face);
  const inward: Vec3 = [-n[0], -n[1], -n[2]];
  const down = restDown(h, axis.len, mouth) - (cover.outMm ?? 0);
  const at1 = (i: number) => Math.round((onFace[i] + inward[i] * down) * 10) / 10;
  const turned = !cover.cover && !unthreaded(axis, moving) && fastenerKind(moving) === 'screw' ? axis.lead : null;
  const inserted = h.through || h.depthMm == null ? axis.len : Math.min(axis.len, h.depthMm);
  const deg = eulerDegFromQuat(holeTurn(axis, inward, !!cover.flip, across));
  // From its tip START_CLEAR_MM out of the mouth to resting: the sunk stretch, then the wood.
  const travel = Math.round((h.sunkMm ?? 0) + inserted + (cover.cover ? BACK_OFF_MM : START_CLEAR_MM));
  return {
    seat: [at1(0), at1(1), at1(2)],
    spin: [deg[0], deg[1], deg[2]].map((d) => Math.round(d * 10) / 10) as unknown as Vec3,
    axis: n,
    travel,
    // TURNED INTO LINE ON THE WAY IN, where something crosses it: a barrel nut goes in with its bore
    // a quarter turn off and is turned until it faces the screw — the gesture its slot is cut for.
    turn: across ? { axis: n, degrees: ACROSS_TURN_DEG } : null,
    // TURNING FROM THE START, through the sunk stretch too: nothing bites there, but a screw that
    // only starts turning halfway in reads as one that slid. Its whole travel at its thread's lead.
    ...(turned ? { drive: 'turned' as const, pitch: turned, depth: bite != null && bite > 0 ? Math.min(travel, Math.round(bite)) : travel }
      : { drive: 'straight' as const, pitch: 0, depth: 0 }),
  };
}

// A SCREW WHOSE THREAD WAS NOT READ ON ITS MODEL — it would go in unturned.
export const unthreaded = (axis: FitAxis, moving: PartItem | undefined) =>
  fastenerKind(moving) === 'screw' && axis.lead == null;
